/* eslint-disable camelcase */
import { Row, Col, Table, Button } from 'antd';
import './index.css';
import { useMediaQuery } from '../../hooks/MediaQuery.hook';
import { getFixtureByName } from '../../apis/fixture.api';
import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import Loader from '../../components/common/Loader';
import { Link, useParams } from 'react-router-dom';
import TypographyTitle from '../../components/common/Title';
import { getTimeDifference } from '../../utils/timeDifference';
import Player from '../player';
import Title from 'antd/es/typography/Title';
import { styles } from '../../styles';
import { HOME_PATH, MENU_CATEGORIES } from '../../utils/constant';
import { PlayCircleOutlined } from '@ant-design/icons';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface Game {
  teamA: string;
  teamB: string;
  teamAImage: string;
  teamBImage: string;
  matchDate: string;
  matchTime: string;
  streamerLinks: StreamerLink[];
  websiteLink?: string;
}

interface StreamerLink {
  streamer: string;
  website: string;
  language: string;
  mobileSupport: string;
  websiteLink: string;
}

interface FixtureData {
  game: Game;
  subCategoryName: string;
}

// ---------------------------------------------------------------------------
// SEO helpers
// ---------------------------------------------------------------------------
function formatMatchLabel(teamA?: string, teamB?: string): string {
  if (!teamA) return 'Live Match';
  return teamB ? `${teamA} vs ${teamB}` : teamA;
}

function formatMatchDate(matchDate?: string): string {
  if (!matchDate) return '';
  const date = new Date(matchDate);
  if (Number.isNaN(date.getTime())) return matchDate;
  return date.toLocaleString('en-PK', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function uniqueValues(values: (string | undefined)[]): string[] {
  return Array.from(new Set(values.filter((v): v is string => Boolean(v))));
}

function setMetaTag(name: string, content: string, attribute = 'name') {
  let meta = document.querySelector<HTMLMetaElement>(`meta[${attribute}="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attribute, name);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

function setCanonical(href: string) {
  let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const FULL_MATCH_DURATION_MS = 10_800_000; // 3 h safety window

function buildTimerLabel(timeDifference: number): string {
  if (timeDifference >= 0) {
    // Countdown to kick-off
    const totalSec = Math.floor(timeDifference / 1000);
    const days = Math.floor(totalSec / 86400);
    const hours = Math.floor((totalSec % 86400) / 3600);
    const minutes = Math.floor((totalSec % 3600) / 60);
    const seconds = totalSec % 60;

    const parts = [
      days > 0 && `${days} day(s)`,
      hours > 0 && `${hours} hour(s)`,
      minutes > 0 && `${minutes} minute(s)`,
      `${seconds} second(s)`,
    ].filter(Boolean);

    return parts.join(', ');
  }

  // Match has started
  if (timeDifference + FULL_MATCH_DURATION_MS < 0) return 'FULL TIME';
  return 'LIVE';
}

// Watch-link renderer — defined once, outside the component
const renderWatchLink = (text: string) => (
  <a href={text} target="_blank" rel="noopener noreferrer">
    <Button
      type="link"
      style={{
        borderRadius: 999,
        border: '1px solid #1677ff',
        padding: '0 14px',
        fontWeight: 500,
      }}
      icon={<PlayCircleOutlined />}
    >
      Watch
    </Button>
  </a>
);

// Column definitions are static — defined at module level, not inside the component
const DESKTOP_COLUMNS = [
  { title: 'Streamer', dataIndex: 'streamer', key: 'streamer' },
  { title: 'Channel', dataIndex: 'website', key: 'website' },
  { title: 'Language', dataIndex: 'language', key: 'language' },
  { title: 'Cross-platform', dataIndex: 'mobileSupport', key: 'mobileSupport' },
  { title: 'Quality', dataIndex: 'quality', key: 'quality', render: () => <p>HD</p> },
  { title: 'Link', dataIndex: 'websiteLink', key: 'websiteLink', render: renderWatchLink },
];

const MOBILE_COLUMNS = [
  { title: 'Streamer', dataIndex: 'streamer', key: 'streamer' },
  { title: 'Channel', dataIndex: 'website', key: 'website' },
  { title: 'Link', dataIndex: 'websiteLink', key: 'websiteLink', render: renderWatchLink },
];

// ---------------------------------------------------------------------------
// Dynamic SEO description (module-level to avoid remounts)
// ---------------------------------------------------------------------------
function FixtureDescription({
  fixture,
  timer,
}: {
  fixture: FixtureData;
  timer: string;
}) {
  const { game, subCategoryName } = fixture;
  const matchLabel = formatMatchLabel(game.teamA, game.teamB);
  const league = subCategoryName || 'Sports';
  const scheduledAt = formatMatchDate(game.matchDate);
  const streamCount = game.streamerLinks?.length ?? 0;
  const streamers = uniqueValues(game.streamerLinks?.map((s) => s.streamer));
  const languages = uniqueValues(game.streamerLinks?.map((s) => s.language));
  const channels = uniqueValues(game.streamerLinks?.map((s) => s.website));
  const mobileFriendlyCount =
    game.streamerLinks?.filter((s) =>
      /yes|true|mobile|all/i.test(s.mobileSupport || ''),
    ).length ?? 0;

  const statusCopy =
    timer === 'LIVE'
      ? `${matchLabel} is LIVE now on SportsFeed24. Switch between the streamer links above for the best free HD experience.`
      : timer === 'FULL TIME'
        ? `${matchLabel} has finished. Check the SportsFeed24 homepage for more live fixtures and upcoming ${league} matches.`
        : `${matchLabel} is scheduled for ${scheduledAt || game.matchTime || 'soon'}. Free stream links usually appear about one hour before kick-off.`;

  return (
    <section className="fixture-seo" aria-label={`${matchLabel} live stream information`}>
      <Title level={2} className="fixture-seo-h2">
        Watch {matchLabel} Live Stream Free on SportsFeed24
      </Title>

      <p className="fixture-seo-text">
        Catch every moment of <strong>{matchLabel}</strong>
        {game.teamB ? ` (${game.teamA} vs ${game.teamB})` : ''} in the{' '}
        <strong>{league}</strong> onSportsfeed24. This fixture page brings you free HD
        live streams, real-time match status, and multiple streamer options so you never miss
        a goal, fight, or race highlight.
      </p>

      <p className="fixture-seo-text">{statusCopy}</p>

      <Title level={3} className="fixture-seo-h3">Match Details</Title>
      <ul className="fixture-seo-list">
        <li>
          <strong>Match:</strong> {matchLabel}
        </li>
        <li>
          <strong>Competition:</strong> {league}
        </li>
        {scheduledAt && (
          <li>
            <strong>Kick-off:</strong> {scheduledAt}
          </li>
        )}
        {game.matchTime && (
          <li>
            <strong>Listed time:</strong> {game.matchTime}
          </li>
        )}
        <li>
          <strong>Status:</strong> {timer || 'Loading…'}
        </li>
        <li>
          <strong>Available streams:</strong>{' '}
          {streamCount > 0
            ? `${streamCount} free stream link${streamCount === 1 ? '' : 's'}`
            : 'Links appear closer to kick-off'}
        </li>
      </ul>

      {streamCount > 0 && (
        <>
          <Title level={3} className="fixture-seo-h3">
            Free Stream Options for {matchLabel}
          </Title>
          <p className="fixture-seo-text">
            SportsFeed24 currently lists <strong>{streamCount}</strong> stream
            {streamCount === 1 ? '' : 's'} for this fixture
            {streamers.length > 0 && (
              <>
                {' '}from sources including <strong>{streamers.slice(0, 5).join(', ')}</strong>
              </>
            )}
            {channels.length > 0 && (
              <>
                {' '}on channels such as <strong>{channels.slice(0, 5).join(', ')}</strong>
              </>
            )}
            {languages.length > 0 && (
              <>
                . Available languages: <strong>{languages.join(', ')}</strong>
              </>
            )}
            . Many links support mobile and desktop viewing
            {mobileFriendlyCount > 0 && (
              <> ({mobileFriendlyCount} marked cross-platform)</>
            )}
            — pick any Watch button above to start streaming in HD.
          </p>
        </>
      )}

      <Title level={3} className="fixture-seo-h3">
        How to Watch {matchLabel} on SportsFeed24
      </Title>
      <ol className="fixture-seo-steps">
        <li>Stay on this page for live {league} coverage of {matchLabel}.</li>
        <li>Use the player above or choose a streamer link from the table.</li>
        <li>If one stream buffers, switch to another free HD option instantly.</li>
        <li>
          Return to the{' '}
          <Link to={HOME_PATH}>SportsFeed24 homepage</Link> for more live fixtures today.
        </li>
      </ol>

      <p className="fixture-seo-text">
       Sportsfeed24 is a free live sports streaming hub. Whether you follow soccer,
        NFL, NBA, UFC, boxing, F1, or other sports, we organise fixtures and streamer links
        so you can watch without subscriptions or registration.
      </p>

      <nav className="fixture-seo-nav" aria-label="More sports categories">
        <Title level={4} className="fixture-seo-h4">Explore More Free Streams</Title>
        <ul className="fixture-seo-category-list">
          {MENU_CATEGORIES.map((cat) => (
            <li key={cat.value}>
              <Link to={`/streams/${cat.value}`}>{cat.label} Streams</Link>
            </li>
          ))}
        </ul>
      </nav>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function Fixture() {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const { teams } = useParams<{ teams: string }>();

  const [fixture, setFixture] = useState<FixtureData | null>(null);
  const [noFixture, setNoFixture] = useState(false);
  const [loader, setLoader] = useState(false);
  const [timer, setTimer] = useState('');

  // Keep a ref to the interval so we can reliably clear it
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Derived — avoids a second useState and keeps streams in sync with fixture
  const streams = useMemo(
    () => fixture?.game?.streamerLinks?.slice(2) ?? [],
    [fixture],
  );

  const hasStreams =
    (fixture?.game?.streamerLinks?.length ?? 0) > 0 || !!fixture?.game?.websiteLink;

  // ---------------------------------------------------------------------------
  // Countdown — uses a ref-based approach to avoid stale-closure on `fixture`
  // ---------------------------------------------------------------------------
  const fixtureRef = useRef<FixtureData | null>(null);
  fixtureRef.current = fixture; // always up to date without re-subscribing

  const startCountdown = useCallback(() => {
    // Clear any previous interval before starting a new one
    if (intervalRef.current) clearInterval(intervalRef.current);

    intervalRef.current = setInterval(() => {
      const currentFixture = fixtureRef.current;
      if (!currentFixture?.game?.matchDate) return;

      const diff = getTimeDifference(currentFixture.game.matchDate);
      const label = buildTimerLabel(diff);
      setTimer(label);

      // Stop ticking once the match is over
      if (label === 'FULL TIME') {
        clearInterval(intervalRef.current!);
        intervalRef.current = null;
      }
    }, 1000);
  }, []); // no deps — reads fixture via ref

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------
  const getFixture = useCallback(async () => {
    if (!teams) return;

    setLoader(true);

    let teamA = teams;
    let teamB = '';
    if (teamA.includes('-vs-')) {
      [teamA, teamB] = teamA.split('-vs-');
    }

    const response = await getFixtureByName({ teamA, teamB });
    const data: FixtureData | undefined = response?.data;

    if (!data?.game) {
      setNoFixture(true);
      setFixture(null);
    } else {
      setNoFixture(false);
      setFixture(data);
    }

    setLoader(false);
  }, [teams]);

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------
  useEffect(() => {
    getFixture();
  }, [teams, getFixture]);

  useEffect(() => {
    if (!fixture?.game) return;

    const matchLabel = formatMatchLabel(fixture.game.teamA, fixture.game.teamB);
    const league = fixture.subCategoryName || 'Sports';
    const title = `Watch ${matchLabel} Live Stream Free – ${league} |Sportsfeed24`;
    const description = `Watch ${matchLabel} live stream free in HD onSportsfeed24. ${league} coverage with real-time updates, multiple streamer links, and free access — no signup required.`;
    const canonical = `https://sportsfeed24.st/fixture/${teams}`;

    document.title = title;
    setMetaTag('description', description);
    setMetaTag('keywords', `${matchLabel}, ${league} live stream, free sports stream,sportsfeed24`);
    setMetaTag('og:title', title, 'property');
    setMetaTag('og:description', description, 'property');
    setMetaTag('og:url', canonical, 'property');
    setCanonical(canonical);

    const schemaId = 'fixture-page-schema';
    document.getElementById(schemaId)?.remove();
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.id = schemaId;
    schemaScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SportsEvent',
      name: matchLabel,
      description,
      startDate: fixture.game.matchDate,
      eventStatus: 'https://schema.org/EventScheduled',
      location: {
        '@type': 'VirtualLocation',
        url: canonical,
      },
      organizer: {
        '@type': 'Organization',
        name: 'SportsFeed24',
        url: 'https://sportsfeed24.st',
      },
      ...(fixture.game.teamB
        ? {
            competitor: [
              { '@type': 'SportsTeam', name: fixture.game.teamA },
              { '@type': 'SportsTeam', name: fixture.game.teamB },
            ],
          }
        : {}),
    });
    document.head.appendChild(schemaScript);

    return () => {
      document.getElementById(schemaId)?.remove();
    };
  }, [fixture, teams]);

  useEffect(() => {
    if (fixture) startCountdown();

    // Cleanup on unmount or when fixture changes
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [fixture, startCountdown]);

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------
  const noFixtureNotice = noFixture && (
    <Row style={{ display: 'flex', justifyContent: 'center' }}>
      <Col span={16}>
        <Title level={3}>
          The Match Between {teams} Is No More Available! Please refer to{' '}
          <Link to={HOME_PATH}>
            <Button style={styles.homeButton}>HOME</Button>
          </Link>{' '}
          Page for Today&apos;s Fixtures!
        </Title>
      </Col>
    </Row>
  );

  const linksComingSoonBanner = !hasStreams && timer !== 'FULL TIME' && (
    <TypographyTitle
      style={{
        display: 'flex',
        justifyContent: 'space-around',
        textAlign: 'center',
        background: 'rgb(46 44 44 / 68%)',
      }}
      level={5}
    >
      <p style={{ color: 'white' }}>
        LINKS WILL BE AVAILABLE 1 HOUR BEFORE THE MATCH STARTS, STAY TUNED!
      </p>
    </TypographyTitle>
  );

  // ---------------------------------------------------------------------------
  // Layout
  // ---------------------------------------------------------------------------
  return (
    <>
      {isDesktop ? (
        <div
          style={{
            paddingTop: '15px',
            textAlign: 'center',
            background: '#464343',
            marginBottom: '5px',
          }}
        >
          {noFixtureNotice}

          <Row>
            <Col span={6}>
              <img
                src={fixture?.game?.teamAImage}
                alt={fixture?.game?.teamA}
                style={styles.imgWidth}
              />
              <p style={{ color: 'white' }}>
                {fixture?.game?.teamB ? fixture.game.teamA : ''}
              </p>
            </Col>

            <Col span={12}>
              <TypographyTitle level={3}>
                <p className="category-name">{fixture?.subCategoryName}</p>
              </TypographyTitle>

              {!fixture?.game?.teamB && (
                <TypographyTitle level={5}>
                  <p style={{ color: 'white' }}>{fixture?.game?.teamA}</p>
                </TypographyTitle>
              )}

              {!timer && (
                <TypographyTitle level={5}>
                  <p className="category-name">
                    {fixture?.game?.teamB
                      ? fixture.game.matchTime
                      : fixture?.game?.matchDate}
                  </p>
                </TypographyTitle>
              )}

              <TypographyTitle level={5}>
                <p style={{ color: '#ffffff', fontSize: '13px' }}>{timer}</p>
              </TypographyTitle>
            </Col>

            <Col span={6}>
              <img
                style={styles.imgWidth}
                src={fixture?.game?.teamBImage}
                alt={fixture?.game?.teamB}
              />
              <p style={{ color: 'white' }}>{fixture?.game?.teamB}</p>
            </Col>
          </Row>

          {linksComingSoonBanner}

          <Row style={styles.flexDisplay}>
            <Col span={16} style={{ marginBottom: '5px' }}>
              {hasStreams && <Player fixture={fixture} />}
            </Col>
          </Row>
        </div>
      ) : (
        <div
          style={{
            display: 'list-item',
            justifyContent: 'space-between',
            textAlign: 'center',
            background: 'rgb(46 44 44 / 68%)',
            marginBottom: '5px',
          }}
        >
          {noFixtureNotice}

          <TypographyTitle level={3}>
            <p className="category-name">{fixture?.subCategoryName}</p>
          </TypographyTitle>

          <Row
            style={{
              display: 'flex',
              paddingRight: '1rem',
              paddingLeft: '1rem',
              justifyContent: 'space-between',
            }}
          >
            <Col>
              <Row>
                <img
                  style={{ width: '40px', textAlign: 'center' }}
                  src={fixture?.game?.teamAImage}
                  alt={fixture?.game?.teamA}
                />
              </Row>
              <Row style={{ color: 'white' }}>
                <p>{fixture?.game?.teamB ? fixture.game.teamA : ''}</p>
              </Row>
            </Col>

            <Col>
              <TypographyTitle level={5}>
                <p style={{ color: '#ffffff', marginBottom: '10px', fontSize: '10px' }}>
                  {timer}
                </p>
              </TypographyTitle>
              <Row>
                <p style={{ color: 'white' }}>
                  {!fixture?.game?.teamB ? fixture?.game?.teamA : ''}
                </p>
              </Row>
              <TypographyTitle level={5}>
                <p className="category-name">
                  {fixture?.game?.teamB
                    ? fixture.game.matchTime
                    : fixture?.game?.matchDate}
                </p>
              </TypographyTitle>
            </Col>

            <Col>
              <Row>
                <img
                  style={{ width: '40px', textAlign: 'center' }}
                  src={fixture?.game?.teamBImage}
                  alt={fixture?.game?.teamB}
                />
              </Row>
              <Row>
                <p style={{ color: 'white' }}>{fixture?.game?.teamB}</p>
              </Row>
            </Col>
          </Row>

          {linksComingSoonBanner}

          {hasStreams && <Player fixture={fixture} />}
        </div>
      )}

      <Table
        className="streams-table"
        dataSource={streams}
        columns={isDesktop ? DESKTOP_COLUMNS : MOBILE_COLUMNS}
        pagination={false}
      />

      {!loader && !noFixture && fixture?.game && (
        <FixtureDescription fixture={fixture} timer={timer} />
      )}

      {loader && <Loader />}
    </>
  );
}